/**
 * Sentry 設定ファイル
 * 本番環境でのエラー監視のための設定
 */

import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import {
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon
} from '@mui/icons-material';

// Sentry SDK type definitions
interface SentryEvent {
  event_id?: string;
  message?: string;
  timestamp?: number;
  level?: 'debug' | 'info' | 'warning' | 'error' | 'critical';
  platform?: string;
  logger?: string;
  server_name?: string;
  release?: string;
  dist?: string;
  environment?: string;
  request?: {
    url?: string;
    method?: string;
    data?: any;
    query_string?: string;
    cookies?: { [key: string]: string };
    headers?: { [key: string]: string };
  };
  user?: {
    id?: string;
    username?: string;
    email?: string;
    ip_address?: string;
    [key: string]: any;
  };
  tags?: { [key: string]: string };
  extra?: { [key: string]: any };
  contexts?: { [key: string]: any };
  breadcrumbs?: Array<{
    timestamp?: number;
    message?: string;
    category?: string;
    level?: 'debug' | 'info' | 'warning' | 'error' | 'critical';
    data?: { [key: string]: any };
    type?: string;
  }>;
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      module?: string;
      thread_id?: number;
      mechanism?: any;
      stacktrace?: any;
    }>;
  };
  [key: string]: any;
}

interface SentryEventHint {
  event_id?: string;
  originalException?: Error;
  syntheticException?: Error;
  data?: any;
  [key: string]: any;
}

interface Integration {
  name: string;
  setupOnce?: () => void;
  [key: string]: any;
}

interface SamplingContext {
  transactionContext: {
    name: string;
    op?: string;
    [key: string]: any;
  };
  parentSampled?: boolean;
  [key: string]: any;
}

// Global type declarations for window.Sentry
declare global {
  interface Window {
    Sentry?: {
      init: (options: {
        dsn: string;
        environment: string;
        tracesSampleRate: number;
        debug: boolean;
        release?: string;
        integrations?: Integration[];
        beforeSend?: (event: SentryEvent, hint: SentryEventHint) => SentryEvent | null;
        tracesSampler?: (samplingContext: SamplingContext) => number;
      }) => void;
      setUser: (user: { id: string; username?: string; email?: string } | null) => void;
      setContext: (key: string, context: any) => void;
      setTag: (key: string, value: string) => void;
      addBreadcrumb: (breadcrumb: {
        message: string;
        category?: string;
        level?: 'debug' | 'info' | 'warning' | 'error' | 'critical';
        data?: any;
        timestamp?: number;
      }) => void;
      captureException: (error: Error, context?: { contexts?: { custom?: any } }) => void;
      captureMessage: (
        message: string,
        context?: {
          level?: 'debug' | 'info' | 'warning' | 'error' | 'critical';
          contexts?: { custom?: any };
        }
      ) => void;
      Integrations: {
        BrowserTracing: new () => Integration;
      };
      ErrorBoundary?: React.ComponentType<any>;
      withErrorBoundary?: (
        Component: React.ComponentType,
        options: {
          fallback: React.ComponentType<any>;
          showDialog: boolean;
        }
      ) => React.ComponentType;
    };
  }
}

export interface SentryConfig {
  dsn: string;
  environment: string;
  enabled: boolean;
  tracesSampleRate: number;
  debug: boolean;
  release?: string;
  integrations?: Integration[];
  beforeSend?: (event: SentryEvent, hint: SentryEventHint) => SentryEvent | null;
}

// デフォルト設定
export const DEFAULT_SENTRY_CONFIG: SentryConfig = {
  dsn: process.env.REACT_APP_SENTRY_DSN || '',
  environment: process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development',
  enabled: process.env.NODE_ENV === 'production' && !!process.env.REACT_APP_SENTRY_DSN,
  tracesSampleRate: 0.1, // 10%のトランザクションをサンプリング
  debug: process.env.NODE_ENV === 'development',
  release: process.env.REACT_APP_VERSION || '1.0.0'
};

// センシティブ情報のマスキング
const sanitizeData = (data: any): any => {
  if (!data) return data;

  const sensitiveKeys = [
    'password',
    'token',
    'refreshToken',
    'accessToken',
    'apiKey',
    'secret',
    'authorization',
    'creditCard',
    'ssn',
    'pin'
  ];

  if (typeof data === 'string') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }

  if (typeof data === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive.toLowerCase()))) {
        sanitized[key] = '[FILTERED]';
      } else if (typeof value === 'object') {
        sanitized[key] = sanitizeData(value);
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }

  return data;
};

// ユーザー情報の設定
export const setSentryUser = (
  user: { id: string; username?: string; email?: string } | null
): void => {
  if (typeof window !== 'undefined' && window.Sentry) {
    const Sentry = window.Sentry;
    if (user) {
      Sentry.setUser({
        id: user.id,
        username: user.username,
        email: user.email
      });
    } else {
      Sentry.setUser(null);
    }
  }
};

// コンテキスト情報の設定
export const setSentryContext = (key: string, context: any): void => {
  if (typeof window !== 'undefined' && window.Sentry) {
    const Sentry = window.Sentry;
    Sentry.setContext(key, sanitizeData(context));
  }
};

// タグの設定
export const setSentryTag = (key: string, value: string): void => {
  if (typeof window !== 'undefined' && window.Sentry) {
    const Sentry = window.Sentry;
    Sentry.setTag(key, value);
  }
};

// ブレッドクラムの追加
export const addSentryBreadcrumb = (breadcrumb: {
  message: string;
  category?: string;
  level?: 'debug' | 'info' | 'warning' | 'error' | 'critical';
  data?: any;
  timestamp?: number;
}): void => {
  if (typeof window !== 'undefined' && window.Sentry) {
    const Sentry = window.Sentry;
    Sentry.addBreadcrumb({
      ...breadcrumb,
      data: sanitizeData(breadcrumb.data),
      timestamp: breadcrumb.timestamp || Date.now() / 1000
    });
  }
};

// エラーのキャプチャ
export const captureException = (error: Error, context?: any): void => {
  if (typeof window !== 'undefined' && window.Sentry) {
    const Sentry = window.Sentry;
    Sentry.captureException(error, {
      contexts: {
        custom: sanitizeData(context)
      }
    });
  }
};

// メッセージのキャプチャ
export const captureMessage = (
  message: string,
  level: 'debug' | 'info' | 'warning' | 'error' | 'critical' = 'info',
  context?: any
): void => {
  if (typeof window !== 'undefined' && window.Sentry) {
    const Sentry = window.Sentry;
    Sentry.captureMessage(message, {
      level,
      contexts: {
        custom: sanitizeData(context)
      }
    });
  }
};

// Sentry初期化関数
export const initSentry = (customConfig?: Partial<SentryConfig>): void => {
  const config = { ...DEFAULT_SENTRY_CONFIG, ...customConfig };

  if (!config.enabled || !config.dsn) {
    console.log('Sentry is disabled or DSN is not configured');
    return;
  }

  // Sentryがロードされているか確認
  if (typeof window !== 'undefined' && window.Sentry) {
    const Sentry = window.Sentry;

    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      tracesSampleRate: config.tracesSampleRate,
      debug: config.debug,
      release: config.release,
      integrations: config.integrations || [
        // デフォルトのインテグレーション
        new Sentry.Integrations.BrowserTracing()
      ],
      beforeSend:
        config.beforeSend ||
        ((event: SentryEvent, hint: SentryEventHint) => {
          // センシティブ情報のフィルタリング
          if (event.request) {
            event.request = sanitizeData(event.request);
          }
          if (event.user) {
            event.user = sanitizeData(event.user);
          }
          if (event.extra) {
            event.extra = sanitizeData(event.extra);
          }
          if (event.contexts) {
            event.contexts = sanitizeData(event.contexts);
          }

          // 開発環境では詳細をコンソールに出力
          if (config.debug) {
            console.log('Sentry Event:', event);
            console.log('Sentry Hint:', hint);
          }

          return event;
        }),
      // パフォーマンス監視の設定
      tracesSampler: (samplingContext: SamplingContext) => {
        // 特定のトランザクションに対してサンプリングレートを調整
        if (samplingContext.transactionContext.name === 'critical-transaction') {
          return 1.0; // 100%サンプリング
        }
        return config.tracesSampleRate;
      }
    });

    console.log('Sentry initialized successfully');
  } else {
    console.error('Sentry SDK not loaded. Please include the Sentry script in your HTML.');
  }
};

// エラーフォールバックコンポーネント
const ErrorFallback: React.FC<{ error: Error; resetError?: () => void }> = ({
  error,
  resetError
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 3
      }}
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: 600,
          width: '100%',
          p: { xs: 4, sm: 6 },
          textAlign: 'center',
          borderRadius: 2
        }}
      >
        <WarningIcon
          sx={{
            fontSize: { xs: 60, sm: 80 },
            color: 'error.main',
            mb: 3
          }}
        />

        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: 'text.primary',
            fontSize: { xs: '1.75rem', sm: '2.125rem' }
          }}
        >
          エラーが発生しました
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: 3,
            maxWidth: 500,
            lineHeight: 1.6
          }}
        >
          申し訳ございません。予期しないエラーが発生しました。
        </Typography>

        {process.env.NODE_ENV === 'development' && error.message && (
          <Box
            component="pre"
            sx={{
              width: '100%',
              p: 2,
              mb: 3,
              bgcolor: 'grey.100',
              border: 1,
              borderColor: 'grey.300',
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 150,
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {error.message}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {resetError && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={resetError}
              sx={{
                minWidth: 150,
                fontWeight: 'bold'
              }}
            >
              もう一度試す
            </Button>
          )}

          <Button
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<HomeIcon />}
            onClick={() => (window.location.href = '/')}
            sx={{
              minWidth: 150,
              fontWeight: 'bold'
            }}
          >
            ホームに戻る
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

// React Error Boundary との統合
export const withSentryErrorBoundary = (Component: React.ComponentType): React.ComponentType => {
  if (typeof window !== 'undefined' && window.Sentry?.withErrorBoundary) {
    const withErrorBoundary = window.Sentry.withErrorBoundary;
    return withErrorBoundary(Component, {
      fallback: ErrorFallback,
      showDialog: false
    });
  }
  return Component;
};
